import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { addAppealFinalVerdict } from "services/appealService";
import {
  AmmendedCourtDecisionType,
  AppealFinalVerdict,
  AppealRequest,
  DefendantAppeal,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  appealData?: AppealRequest;
  defendantAppealData?: DefendantAppeal;
  inline?: boolean;
  onClose: (refetch: boolean) => void;
};

const defaultValue: AppealFinalVerdict = {
  appeal_request: 0,
  court_decision_summary: "",
  amended_court_decision: "",
  amended_court_decision_amount: "",
  prosecutor_feedback_on_decision: "",
};

const AppealAddFinalVerdict: FC<Props> = ({
  appealData,
  defendantAppealData,
  inline,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<AppealFinalVerdict>({
    ...defaultValue,
    appeal_request: appealData?.id,
    defendant_appeal_request: defendantAppealData?.id,
  });

  useEffect(() => {
    // Fetch any necessary base data for final verdict
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const defendants =
    appealData?.defendants || defendantAppealData?.defendants || [];

  const selectedDefendantIds = Array.isArray(formState?.defendant_ids)
    ? formState.defendant_ids.map((d: any) =>
        typeof d === "object" ? String(d.uu_id ?? d.id) : String(d),
      )
    : [];

  const toggleDefendant = (id: string) => {
    setFormState((prev) => {
      const current = Array.isArray(prev?.defendant_ids)
        ? prev.defendant_ids.map((d: any) =>
            typeof d === "object" ? String(d.uu_id ?? d.id) : String(d),
          )
        : [];
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      return { ...prev, defendant_ids: next };
    });
  };

  const validateForm = () => {
    if (!formState.court_decision_summary) {
      return t("court_decision_summary_required");
    }
    if (!formState.amended_court_decision_type) {
      return t("amended_court_decision_type_required");
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);

    addAppealFinalVerdict({
      ...formState,
      defendant_name:
        appealData?.defendent_name_if_bail_right ||
        defendantAppealData?.defendent_name_if_bail_right ||
        "",
    })
      .then(() => {
        showTooltip(t("successfully_submitted"), "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  return (
    <div
      style={inline ? undefined : { marginTop: "0px" }}
      className={
        inline
          ? "w-full"
          : "fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      }
      onClick={inline ? undefined : handleClickOutside}
    >
      <div
        className={
          inline
            ? "relative flex w-full flex-col rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden"
            : "relative flex w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-white shadow-xl"
        }
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-1.5 rounded-full bg-red-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t("appeal_final_verdict_form")}
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                {t("fill_final_verdict_details")}
              </p>
            </div>
          </div>
          {!inline && (
            <button
              type="button"
              onClick={() => onClose(false)}
              aria-label="Close"
              className="shrink-0 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Defendant selection — professional card checkboxes */}
            {defendants.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t("select_count_defendant")}
                  </label>
                  {selectedDefendantIds.length > 0 && (
                    <span className="text-xs font-medium text-red-600">
                      {selectedDefendantIds.length} {t("selected", { defaultValue: "selected" })}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
                  {defendants.map((def: any) => {
                    const id = String(def?.id);
                    const selected = selectedDefendantIds.includes(id);
                    const name = [
                      def?.defendant_info?.first_name,
                      def?.defendant_info?.middle_name,
                      def?.defendant_info?.last_name,
                    ]
                      .filter(Boolean)
                      .join(" ");
                    const initials = name
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((p: string) => p[0])
                      .join("")
                      .toUpperCase();
                    return (
                      <label
                        key={id}
                        className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${
                          selected
                            ? "border-red-300 bg-red-50"
                            : "border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleDefendant(id)}
                          className="h-4 w-4 rounded text-red-600 focus:ring-red-400"
                        />
                        <div
                          className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${
                            selected
                              ? "bg-red-600 text-white"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {initials || "?"}
                        </div>
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {name || t("n_a")}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("court_decision_type")}
                <span className="text-red-500"> *</span>
              </label>
              <select
                className="capitalize border border-gray-300 rounded-lg bg-white p-2 pr-8 focus:outline-none focus:ring focus:ring-red-200"
                name="amended_court_decision_type"
                value={formState?.amended_court_decision_type ?? ""}
                onChange={handleChange}
              >
                <option value={""}>{t("select_court_decision_type")}</option>
                {Object.entries(AmmendedCourtDecisionType).map(
                  ([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ),
                )}
              </select>
            </div>

            {!["UPHELD", "REMANDED"].includes(
              formState?.amended_court_decision_type || "",
            ) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    {t("amended_court_decision")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("amended_court_decision_placeholder")}
                    className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                    name="amended_court_decision"
                    value={formState?.amended_court_decision || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-1">
                    {t("amended_court_decision_amount")}
                  </label>
                  <input
                    type="text"
                    placeholder={t("amended_court_decision_amount_placeholder")}
                    className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                    name="amended_court_decision_amount"
                    value={formState?.amended_court_decision_amount || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("prosecutor_feedback_on_decision")}
              </label>
              <textarea
                rows={3}
                placeholder={t("prosecutor_feedback_on_decision_placeholder")}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                name="prosecutor_feedback_on_decision"
                value={formState?.prosecutor_feedback_on_decision || ""}
                onChange={handleChange}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">
                {t("court_decision_summary")}
                <span className="text-red-500"> *</span>
              </label>
              <textarea
                rows={3}
                placeholder={t("court_decision_summary_placeholder")}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-red-200"
                name="court_decision_summary"
                value={formState?.court_decision_summary || ""}
                onChange={handleChange}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
              >
                {t("cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={loading}
              >
                {loading ? (
                  <>
                    {LoadingIcon}
                    {t("loading")}
                  </>
                ) : (
                  t("save")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppealAddFinalVerdict;
